/** The prior art library's own writes: the attorney's type on a file, and a way to open it. */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  FileResponse,
  LiteratureType,
  TaskResponse,
} from "@patent-studio/api-types";
import { classifyFile, replaceFile } from "@/services/files";
import { taskDetailKey } from "./query-keys";
import { dropFileFromTask } from "./use-files";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";

/**
 * Set or clear one file's literature type.
 *
 * The row is patched in the cache from the server's own answer rather than
 * refetched: the status stream is already driving this cache, and a refetch
 * that lands mid-processing would put a file's status back.
 */
export function useClassifyFile(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.fileClassify,
    mutationFn: ({
      fileId,
      literatureType,
    }: {
      fileId: string;
      literatureType: LiteratureType | null;
    }) => classifyFile(taskId, fileId, literatureType),
    onSuccess: (served: FileResponse) => {
      qc.setQueryData<TaskResponse>(taskDetailKey(taskId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          files: prev.files.map((f) =>
            f.file_id === served.file_id
              ? { ...f, literature_type: served.literature_type }
              : f,
          ),
        };
      });
    },
  });
}

/**
 * Replace a document the worker could not read with a better copy.
 *
 * One request: the server stores the new copy and puts it in the old one's
 * place in a single write, so a refused replacement leaves the library as it
 * was and there is nothing to reread. The new file is a new reference with
 * its own number.
 */
export function useReplaceFile(taskId: string, onDone: () => Promise<void>) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.fileReplace,
    mutationFn: ({
      fileId,
      replacement,
    }: {
      fileId: string;
      replacement: File;
    }) => replaceFile(taskId, fileId, replacement).then(() => fileId),
    // The old copy goes at once; the new one is read off the task on the refresh.
    onSuccess: async (fileId: string) => {
      dropFileFromTask(qc, taskId, fileId);
      await onDone();
    },
  });
}
